import { Paper } from '@sanlam-glacier/app-common/components';
import { useSuspenseQuery } from '@tanstack/react-query';
import { getEditProposalInformation, IAPPROPOSALSTATUS, SecurityAccessFeaturesEnum } from 'bff';
import { PageHeader } from 'components/atoms/page-header/PageHeader';
import { LoadingPage } from 'components/molecules/loading-page/LoadingPage';
import { ProposalStepper } from 'modules/new-business/components/organisms/proposal-stepper/ProposalStepper';
import { RouteBoundary } from 'components/organisms/route-boundary/RouteBoundary';
import { SomethingWentWrong } from 'modules/app/components/pages/something-went-wrong/SomethingWentWrong';
import { useGetProposalCurrentStep } from 'hooks/useGetProposalCurrentStep';
import { useProposalIdFromParams } from 'hooks/useProposalIdFromParams';
import { useConfig } from 'providers/config/ConfigProvider';
import { useMemo } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { assertIsDefined } from '@sanlam-glacier/app-common/utils';
import { getAllowedSteps } from 'modules/new-business/utils/proposal-form-state/proposal-form-state.function';
import { proposalStepToRoute } from 'modules/new-business/utils/proposal-stepper/proposal-route-mapping';
import styles from './proposal-layout.module.scss';
import { useUserContext } from 'providers/managers/user-context-manager/user-context-manager';
import { DisabledFieldsProvider } from 'providers/disabled-provider/DisabledFieldsProvider';
import { useLocalStorage } from 'hooks/useLocalStorage';
import { useIsBackgroundSubmitting } from 'hooks/useIsGlobalFetching';

export const EditProposalLayout = () => {
  const proposalId = useProposalIdFromParams();
  const currentStep = useGetProposalCurrentStep();
  const {
    featureFlags: { stepperFeatureToggle }
  } = useConfig();
  const [overrideStepper] = useLocalStorage('overrideStepper', stepperFeatureToggle);

  assertIsDefined(proposalId, 'Expected proposal ID to be defined');

  const {
    data: { selectedScenarioId, stateModel, isProposalLocked, status }
  } = useSuspenseQuery(getEditProposalInformation({ proposalId }));

  const userContext = useUserContext();

  const userHasEditFeature =
    userContext
      .getUserFeatures()
      .findIndex((feature) => feature === SecurityAccessFeaturesEnum.EditProposal) > -1;

  const isBackgroundSubmitting = useIsBackgroundSubmitting();
  const disableInputs = !userHasEditFeature || isProposalLocked || isBackgroundSubmitting;

  let maxStep = getAllowedSteps(stateModel!);
  maxStep = !!selectedScenarioId && maxStep > 3 ? 3 : maxStep;

  if (overrideStepper) maxStep = 6;

  // biome-ignore lint/correctness/useExhaustiveDependencies: The max step is explicitly omitted to prevent an unwanted navigation away from the current step, should the max step change
  const redirectUri = useMemo(() => {
    // Rule for showing Step 5, even though Step 6 is active to allow for Download & Sign, when the proposal has an incomplete Application
    if (!currentStep && maxStep === 6 && status === IAPPROPOSALSTATUS.ApplicationInProgress)
      return proposalStepToRoute(5);
    if (!currentStep || currentStep > maxStep) return proposalStepToRoute(maxStep);

    return undefined;
  }, [currentStep]);

  return (
    <DisabledFieldsProvider value={disableInputs}>
      <PageHeader>Proposal #{proposalId}</PageHeader>
      <Paper rounding={Paper.ROUNDING.SMALL} className={styles.stepContent}>
        <ProposalStepper currentStep={currentStep ?? maxStep} />
        <RouteBoundary errorFallback={<SomethingWentWrong />} loadingFallback={<LoadingPage />}>
          {redirectUri ? <Navigate to={redirectUri} relative='path' replace /> : <Outlet />}
        </RouteBoundary>
      </Paper>
    </DisabledFieldsProvider>
  );
};

export default EditProposalLayout;
